import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { Trophy, Flame, Search, Sparkles, TrendingUp, User, ShieldAlert } from 'lucide-react';
import './Leaderboard.css';

const Leaderboard = ({ currentUser }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/stats/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlayers(res.data || []);
        setError('');
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setError('Не вдалося завантажити таблицю лідерів');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  const filteredPlayers = players.filter(player => {
    const fullName = `${player.studentName} ${player.studentLastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           (player.studentCity && player.studentCity.toLowerCase().includes(searchTerm.toLowerCase())) ||
           (player.position && player.position.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={22} className="rank-trophy gold" />;
    if (rank === 2) return <Trophy size={20} className="rank-trophy silver" />;
    if (rank === 3) return <Trophy size={18} className="rank-trophy bronze" />;
    return <span className="rank-number-text">{rank}</span>;
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'trainee': return 'Стажер';
      case 'junior': return 'Молодший';
      case 'regular': return 'Фахівець';
      case 'senior': return 'Старший';
      default: return 'Стажер';
    }
  };

  const isSelf = (player) => {
    if (!currentUser) return false;
    // Check if names and city match
    const currentName = currentUser.studentProfile?.studentName || currentUser.username.split(/[._\s]/)[0];
    const currentLastName = currentUser.studentProfile?.studentLastName || currentUser.username.split(/[._\s]/)[1] || '';
    const currentCity = currentUser.city || '';
    
    return player.studentName.trim().toLowerCase() === currentName.trim().toLowerCase() &&
           player.studentLastName.trim().toLowerCase() === currentLastName.trim().toLowerCase() &&
           (player.studentCity || '').trim().toLowerCase() === currentCity.trim().toLowerCase();
  };

  return (
    <div className="leaderboard-card-container">
      <div className="leaderboard-header-section">
        <div className="leaderboard-title-group">
          <Trophy className="title-icon-main" size={28} />
          <div>
            <h2>Рейтинг Гравців</h2>
            <p>Найкращі співробітники за кількістю набраних XP</p>
          </div>
        </div>
        <div className="leaderboard-search-box">
          <Search size={16} className="search-icon-inside" />
          <input
            type="text"
            placeholder="Пошук за ім'ям, містом або посадою..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="leaderboard-error-box">
          <ShieldAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="leaderboard-loading-box">
          <div className="pulse-loader" />
          <span>Завантаження таблиці лідерів...</span>
        </div>
      ) : filteredPlayers.length === 0 ? (
        <div className="leaderboard-empty-box">
          <User size={36} className="empty-icon" />
          <span>Нікого не знайдено</span>
        </div>
      ) : (
        <div className="leaderboard-table-wrapper">
          <div className="leaderboard-table-header">
            <div className="col-rank">Місце</div>
            <div className="col-player">Гравець</div>
            <div className="col-city">Місто / Посада</div>
            <div className="col-streak">Серія</div>
            <div className="col-badges">Досягнення</div>
            <div className="col-xp">Досвід (XP)</div>
          </div>
          <div className="leaderboard-table-body">
            {filteredPlayers.map((player, index) => {
              const rank = index + 1;
              const self = isSelf(player);
              return (
                <div key={player._id} className={`leaderboard-row ${self ? 'is-self' : ''} ${rank <= 3 ? `top-${rank}` : ''}`}>
                  <div className="col-rank">
                    <div className="rank-badge-wrapper">
                      {getRankIcon(rank)}
                    </div>
                  </div>
                  <div className="col-player">
                    <div className="player-profile-cell">
                      <div className="player-avatar">
                        {player.studentName[0]}{player.studentLastName[0]}
                        {rank <= 3 && <Sparkles size={10} className="spark-decor" />}
                      </div>
                      <div className="player-info-meta">
                        <span className="player-fullname">
                          {player.studentName} {player.studentLastName} {self && <span className="self-tag">(Ви)</span>}
                        </span>
                        <span className="player-level-badge">{getLevelLabel(player.level)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-city">
                    <div className="city-pos-cell">
                      <span className="city-text">📍 {player.studentCity || '—'}</span>
                      <span className="position-text">{player.position === 'waiter' ? 'Офіціант' : player.position === 'bartender' ? 'Бармен' : player.position}</span>
                    </div>
                  </div>
                  <div className="col-streak">
                    {player.streakDays > 0 ? (
                      <div className="streak-badge-active" title={`Активний ліміт проходжень: ${player.streakDays} дн.`}>
                        <Flame size={16} className="streak-flame-icon" />
                        <span>{player.streakDays} дн.</span>
                      </div>
                    ) : (
                      <span className="no-streak-text">—</span>
                    )}
                  </div>
                  <div className="col-badges">
                    <div className="badges-list-chips">
                      {(player.badges || []).map((badge, bIdx) => (
                        <span key={bIdx} className={`badge-chip-item ${badge === 'Відмінник' ? 'gold' : badge === 'Марафонець' ? 'streak' : ''}`}>
                          {badge === 'Відмінник' ? '⭐' : badge === 'Марафонець' ? '🔥' : badge === 'Профі' ? '🏆' : '⚡'} {badge}
                        </span>
                      ))}
                      {(!player.badges || player.badges.length === 0) && <span className="empty-badge-dots">—</span>}
                    </div>
                  </div>
                  <div className="col-xp">
                    <div className="xp-badge-display">
                      <TrendingUp size={14} className="xp-trending-icon" />
                      <strong>{player.xp || 0}</strong>
                      <span className="xp-unit">XP</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
